import React from 'react';
import { mdiCash } from '@mdi/js';
import { Button, TextField } from '@mollybet/ui';
import { Currency } from 'common/classes/currency';
import {
  TotalRow,
  TotalCell,
  ColumnProp,
  BaseTable,
} from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { CurrencyCodes } from 'common/configs/currency';
import { Xrates } from 'common/state/accounts/actions';
import { ProviderSummary } from 'common/state/provider/actions';
import { useMemo } from 'react';
import { ModalModes } from '../provider';
import { AdjustmentModalProps } from './adjustmentModal';

export interface StatementsTableProps {
  summaries: ProviderSummary[];
  xrates: Xrates;
  providerSummaryEndDate: string;
  setModalMode: (mode: ModalModes) => void;
  setInitialAdjustmentData: (
    data: AdjustmentModalProps['initialData'] | null
  ) => void;
  setProviderSummaryEndDate: (date: string) => void;
  isLoading?: boolean;
}

export const SubRowGenerator =
  (_xrates: Xrates, summaries: ProviderSummary[]) =>
  ({ index, style }) => {
    if (!summaries || index !== summaries.length - 1) return null;

    const totalBalance = summaries.reduce(
      (acc, summary) => acc + (summary.net_balance_gbp || 0),
      0
    );

    return (
      <TotalRow role="row" style={style}>
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell>Total</TotalCell>
        <TotalCell role="cell">
          {new Currency(totalBalance, CurrencyCodes.GBP).toElement()}
        </TotalCell>
      </TotalRow>
    );
  };

export const StatementsTable = ({
  summaries,
  xrates,
  providerSummaryEndDate,
  setProviderSummaryEndDate,
  setModalMode,
  setInitialAdjustmentData,
  isLoading,
}: StatementsTableProps) => {
  const columns = useMemo(
    (): ColumnProp<ProviderSummary>[] => [
      { key: 'ccy_code', title: 'Currency' },
      {
        key: 'totus_bookie_deposits',
        title: 'Totus Deposits',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.totus_bookie_deposits, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.totus_bookie_deposits, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'customer_bookie_deposits',
        title: 'Customer Deposits',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.customer_bookie_deposits, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.customer_bookie_deposits, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'pl',
        title: 'Profit/Loss',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.pl, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.pl, row.ccy_code).toString({ symbol: false }),
      },
      {
        key: 'pl_settled',
        title: 'Profit/Loss Settled',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.pl_settled, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.pl_settled, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'comms',
        title: 'Commissions',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.comms, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.comms, row.ccy_code).toString({ symbol: false }),
      },
      {
        key: 'singbet_dividend',
        title: 'Araco - SingBet `dividend`',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.singbet_dividend, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.singbet_dividend, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'commission_settled',
        title: 'Commission Settled',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.commission_settled, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.commission_settled, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'provider_adjustment',
        title: 'Adjustments',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.provider_adjustment, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.provider_adjustment, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'net_balance',
        title: 'Net Balance',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.net_balance, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.net_balance, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'net_balance_gbp',
        title: 'Net Balance (GBP)',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.net_balance_gbp, CurrencyCodes.GBP).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.net_balance_gbp, CurrencyCodes.GBP).toString({
            symbol: false,
          }),
      },
      {
        key: 'pl_settlement_threshold',
        title: 'Profit/Loss Settlement Threshold',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.pl_settlement_threshold, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.pl_settlement_threshold, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'comms_settlement_threshold',
        title: 'Commissions Settlement Threshold',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(
            row.comms_settlement_threshold,
            row.ccy_code
          ).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.comms_settlement_threshold, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'actions',
        title: 'Actions',
        valueFormatter: (row: ProviderSummary) => (
          <Button
            onClick={() => {
              setInitialAdjustmentData({
                sourceClientCode: `provider.${row.id}.provider_adjustment.${row.ccy_code}`,
              });
              setModalMode('adjustment');
            }}
          >
            Adjust
          </Button>
        ),
      },
    ],
    [setInitialAdjustmentData, setModalMode]
  );

  const csvExtra = useMemo(() => {
    if (!summaries) return null;

    const totalBalance = summaries.reduce(
      (acc, summary) => acc + (summary.net_balance_gbp || 0),
      0
    );

    return `,,,,,,,,,,"${new Currency(totalBalance, CurrencyCodes.GBP).toString(
      {
        symbol: 'code',
      }
    )}"`;
  }, [summaries]);

  return (
    <>
      <TableTitle icon={mdiCash} title="Statements" />
      <BaseTable
        columns={columns}
        rows={summaries || []}
        csvName="Statements"
        csvExtra={csvExtra}
        paginate={false}
        isLoading={isLoading}
        SubRow={SubRowGenerator(xrates, summaries)}
        extraButtons={
          <TextField
            type="date"
            value={providerSummaryEndDate}
            label="To"
            onChange={(event) =>
              setProviderSummaryEndDate(event.currentTarget.value)
            }
          />
        }
      />
    </>
  );
};
